/**
 * Insights Store
 *
 * Zustand store that owns the global filter bar state shared by every
 * page in the Insights feature (time range, comparison, app filter,
 * ambiguity toggle), plus a saved-views slice the Cohort Builder and
 * individual page headers can use.
 *
 * Structure mirrors `features/signups/store.ts` for consistency with the
 * neighbouring feature.  URL serialisation lets operators bookmark a fully
 * filtered Insights view.
 */

import { create } from 'zustand'
import type { Granularity, InsightsFilters, SavedView, TimeRange } from './types'

// ── Default filter shape ────────────────────────────────────────────────────

const DEFAULT_FILTERS: InsightsFilters = {
  time_range: { kind: 'preset', preset: '30d' },
  comparison_range: undefined,
  // Empty = all apps, INCLUDING the published `Unmapped` sentinel. That is
  // the correct default: `Unmapped` is real traffic and revenue, and
  // defaulting it out would silently shrink every portfolio total.
  app_ids: [],
  // Empty = all countries, including the `Unavailable` sentinel.
  country_codes: [],
  exclude_ambiguous: true,
  granularity: 'day',
}

// ── State / Actions ─────────────────────────────────────────────────────────

interface InsightsStoreState {
  filters: InsightsFilters
  savedViews: SavedView[]
  /** Refresh counter — bump to make subscribed pages re-fetch. */
  refreshNonce: number
  /** Last time the global Refresh button was pressed (epoch ms). */
  lastRefreshAt: number | null
}

interface InsightsStoreActions {
  setTimeRange(range: TimeRange): void
  setComparisonRange(range: TimeRange | undefined): void
  toggleComparison(enabled: boolean): void
  setAppIds(appIds: string[]): void
  toggleAppId(appId: string): void
  setCountryCodes(codes: string[]): void
  setGranularity(granularity: Granularity): void
  setExcludeAmbiguous(value: boolean): void
  resetFilters(): void
  triggerRefresh(): void

  // Saved views
  saveView(view: Omit<SavedView, 'id' | 'created_at'>): SavedView
  deleteView(id: string): void
  applyView(id: string): void

  // URL sync
  hydrateFromUrl(params: URLSearchParams): void
  serializeToUrl(): URLSearchParams
}

type InsightsStore = InsightsStoreState & InsightsStoreActions

// ── Helpers ─────────────────────────────────────────────────────────────────

function makeId(): string {
  return `view_${Math.random().toString(36).slice(2, 10)}`
}

function parseTimeRange(s: string | null): TimeRange | null {
  if (!s) return null
  if (s.startsWith('preset:')) {
    const preset = s.slice('preset:'.length) as TimeRange['preset']
    if (preset && ['7d', '14d', '30d', '90d'].includes(preset)) {
      return { kind: 'preset', preset }
    }
    return null
  }
  if (s.startsWith('custom:')) {
    const [from, to] = s.slice('custom:'.length).split('|')
    if (from && to) return { kind: 'custom', from, to }
  }
  return null
}

function serializeTimeRange(tr: TimeRange): string {
  if (tr.kind === 'preset' && tr.preset) return `preset:${tr.preset}`
  if (tr.kind === 'custom') return `custom:${tr.from ?? ''}|${tr.to ?? ''}`
  return ''
}

// ── Store ───────────────────────────────────────────────────────────────────

export const useInsightsStore = create<InsightsStore>((set, get) => ({
  filters: DEFAULT_FILTERS,
  savedViews: [],
  refreshNonce: 0,
  lastRefreshAt: null,

  setTimeRange: (range) => {
    set((s) => ({ filters: { ...s.filters, time_range: range } }))
  },

  setComparisonRange: (range) => {
    set((s) => ({ filters: { ...s.filters, comparison_range: range } }))
  },

  toggleComparison: (enabled) => {
    set((s) => ({
      filters: {
        ...s.filters,
        comparison_range: enabled
          ? s.filters.comparison_range ?? { kind: 'preset', preset: '30d' }
          : undefined,
      },
    }))
  },

  setAppIds: (appIds) => {
    set((s) => ({ filters: { ...s.filters, app_ids: appIds } }))
  },

  toggleAppId: (appId) => {
    set((s) => {
      const current = new Set(s.filters.app_ids)
      if (current.has(appId)) current.delete(appId)
      else current.add(appId)
      return { filters: { ...s.filters, app_ids: Array.from(current) } }
    })
  },

  setCountryCodes: (codes) => {
    set((s) => ({ filters: { ...s.filters, country_codes: codes } }))
  },

  setGranularity: (granularity) => {
    set((s) => ({ filters: { ...s.filters, granularity } }))
  },

  setExcludeAmbiguous: (value) => {
    set((s) => ({ filters: { ...s.filters, exclude_ambiguous: value } }))
  },

  resetFilters: () => {
    set({ filters: DEFAULT_FILTERS })
  },

  triggerRefresh: () => {
    set((s) => ({ refreshNonce: s.refreshNonce + 1, lastRefreshAt: Date.now() }))
  },

  saveView: (view) => {
    const created: SavedView = { ...view, id: makeId(), created_at: new Date().toISOString() }
    set((s) => ({ savedViews: [...s.savedViews, created] }))
    return created
  },

  deleteView: (id) => {
    set((s) => ({ savedViews: s.savedViews.filter((v) => v.id !== id) }))
  },

  applyView: (id) => {
    const view = get().savedViews.find((v) => v.id === id)
    if (view) set({ filters: view.filters })
  },

  hydrateFromUrl: (params) => {
    const filters: InsightsFilters = { ...DEFAULT_FILTERS }

    const tr = parseTimeRange(params.get('time_range'))
    if (tr) filters.time_range = tr

    const cmp = parseTimeRange(params.get('comparison_range'))
    if (cmp) filters.comparison_range = cmp

    const appIds = params.get('app_ids')
    if (appIds) filters.app_ids = appIds.split(',').filter(Boolean)

    const countryCodes = params.get('country_codes')
    if (countryCodes) filters.country_codes = countryCodes.split(',').filter(Boolean)

    const granularity = params.get('granularity')
    if (granularity === 'day' || granularity === 'week' || granularity === 'month') {
      filters.granularity = granularity
    }

    const excl = params.get('exclude_ambiguous')
    if (excl !== null) filters.exclude_ambiguous = excl === 'true'

    set({ filters })
  },

  serializeToUrl: () => {
    const { filters } = get()
    const params = new URLSearchParams()
    params.set('time_range', serializeTimeRange(filters.time_range))
    if (filters.comparison_range) {
      params.set('comparison_range', serializeTimeRange(filters.comparison_range))
    }
    if (filters.app_ids.length > 0) params.set('app_ids', filters.app_ids.join(','))
    if (filters.country_codes.length > 0) {
      params.set('country_codes', filters.country_codes.join(','))
    }
    params.set('granularity', filters.granularity)
    params.set('exclude_ambiguous', String(filters.exclude_ambiguous))
    return params
  },
}))

// ── Selectors ───────────────────────────────────────────────────────────────

export const selectFilters = (s: InsightsStore): InsightsFilters => s.filters
export const selectAppIds = (s: InsightsStore): string[] => s.filters.app_ids
export const selectRefreshNonce = (s: InsightsStore): number => s.refreshNonce
